import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  ViewRef,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { CartActions } from '@spartacus/cart/base/core';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import { GlobalMessageService, GlobalMessageType, RoutingService } from '@spartacus/core';
import { BreakpointService } from '@spartacus/storefront';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subscription } from 'rxjs';
import { concatMap, distinctUntilChanged, filter, map, take, tap } from 'rxjs/operators';
import { CUSTOM_LAUNCH_CALLER } from 'src/app/dialog/custom-launch-dialog-config';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { DealerPriceService } from 'src/app/service/dealer-price/dealer-price.service';
import { GoogleTagManagerService } from 'src/app/service/googletagmanager/google-tag-manager.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { StandardModalService } from 'src/app/service/modal/standard-modal.service';
import { PlpPopupService } from 'src/app/service/plp-popup/plp-popup.service';
import { CustomProductService } from 'src/app/service/product/custom-product.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { BaseSiteConfigService } from '../../../../service/base-site/base-site-config.service';
import { OrderHistoryService } from '../../../../service/orders/order-history.service';
import { ExplodedViewProductListingComponent } from './exploded-view-product-listing.component';

@Component({
  selector: 'app-exploded-view-popover-product-listing',
  templateUrl: './exploded-view-popover-product-listing.component.html',
  styleUrls: ['./exploded-view-popover-product-listing.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ExplodedViewPopoverProductListingComponent extends ExplodedViewProductListingComponent implements OnInit {
  @ViewChild('popoverProductListing', { static: false }) popoverProductListing: ElementRef;
  basepriceBehaviorConfigs$: Subscription;
  isBackOrderPopupEnabled: boolean = true;
  backOrderModalSubscription: Subscription;
  plpPopupModalEnabled: Subscription;
  isPlpPopupEnabled: boolean;
  isDiscontinuedEnabledSubscription$: Subscription;
  isDiscontinuedEnabled: boolean;

  constructor(
    dealerPriceService: DealerPriceService,
    componentService: ComponentService,
    router: Router,
    routingService: RoutingService,
    cdr: ChangeDetectorRef,
    activeCartService: ActiveCartFacade,
    actionsSubject: ActionsSubject,
    messageService: GlobalMessageService,
    breakpointService: BreakpointService,
    countryService: ShimanoCountryService,
    checkoutServiceExtends: CheckoutServiceExtends,
    activeLanguage: ShimanoLanguageService,
    customProductService: CustomProductService,
    currentBusinesstype: CurrentBusinessType,
    deviceDetectorService: DeviceDetectorService,
    gtmService: GoogleTagManagerService,
    baseSiteConfigService: BaseSiteConfigService,
    orderHistoryService: OrderHistoryService,
    modalService: StandardModalService,
    el: ElementRef,
    vcr: ViewContainerRef,
    storageService: StorageService,
    plpPopupService: PlpPopupService
  ) {
    super(
      dealerPriceService,
      componentService,
      router,
      routingService,
      cdr,
      activeCartService,
      actionsSubject,
      messageService,
      breakpointService,
      countryService,
      checkoutServiceExtends,
      activeLanguage,
      customProductService,
      currentBusinesstype,
      deviceDetectorService,
      gtmService,
      baseSiteConfigService,
      orderHistoryService,
      modalService,
      el,
      vcr,
      storageService,
      plpPopupService
    );
  }

  ngOnInit() {
    this.checkDesktopWidth();
    this.isPlpPopupModalEnabled();
    this.getInitSrpToggleValue();
    this.isDiscontinuedEnabledSubscription$ = this.componentService.discontinuedEnabled$.subscribe((status) => {
      this.isDiscontinuedEnabled = status;
    });
    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrpOnly = checked;
      this.cdr.detectChanges();
    });
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.clearSelectedProducts();
    });
    this.basepriceBehaviorConfigs$ = this.dealerPriceService.getBasepriceBehavior$().subscribe((hideBaseprice: any) => {
      if (hideBaseprice === 'true') {
        this.basepriceHiddens = true;
      } else {
        this.basepriceHiddens = false;
      }
      this.cdr.detectChanges();
    });

    // add to cart event
    this.cartAddEntrySuccessSubscription$ = this.actionsSubject
      .pipe(ofType(CartActions.CART_ADD_ENTRY_SUCCESS, CartActions.CART_ADD_ENTRY_FAIL))
      .pipe(
        map((cartAddEntryStatus: any) => {
          if (cartAddEntryStatus.type === CartActions.CART_ADD_ENTRY_SUCCESS) {
            this.hasCartAddEntrySuccess = true;
          } else {
            this.cartAddEntryFail = true;
          }
          return cartAddEntryStatus;
        }),
        concatMap(() => this.activeCartService.isStable().pipe(distinctUntilChanged())),
        tap(() => this.messageService.remove(GlobalMessageType.MSG_TYPE_CONFIRMATION))
      )
      .subscribe((isCartLoaded) => {
        if (isCartLoaded) {
          this.add2CartBtn.classList.remove('rotating', 'disabled');
          this.showSpinnerPopover = false;
          this.componentService.setMiniCartLoading(false);
          const productCodeAddedToCart = this.componentService.productCodeJustAddedToCart$.getValue();
          if (this.hasCartAddEntrySuccess && productCodeAddedToCart) {
            this.showGlobalMessage(
              'cart.addToCartSuccess',
              GlobalMessageType.MSG_TYPE_CONFIRMATION,
              productCodeAddedToCart
            );
            this.hasCartAddEntrySuccess = false;
          }
        } else {
          this.componentService.setMiniCartLoading(true);
        }
        if (!(this.cdr as ViewRef).destroyed) {
          this.cdr.detectChanges();
        }
      });

    this.isBackOrderModalEnabled();
  }

  get productsCountInPopover$(): Observable<number> {
    return this.componentService.productsCountInPopover$;
  }

  isPlpPopupModalEnabled = () => {
    this.plpPopupModalEnabled = this.plpPopupService.getPlpPopupEnabledConfig$().subscribe((plpPopup) => {
      this.isPlpPopupEnabled = plpPopup.toString() === 'true';
      this.cdr.detectChanges();
    });
  };

  isBackOrderModalEnabled = () => {
    this.backOrderModalSubscription = this.getBackOrderModalConfig$().subscribe((backOrder) => {
      this.isBackOrderPopupEnabled = backOrder === 'true';
      this.cdr.detectChanges();
    });
  };

  getBackOrderModalConfig$(): Observable<string> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.backorder.popup.message'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs
              .filter((config: any) => config.key === 'shimano.backorder.popup.message')
              .map((res: any) => res.value)[0]
        )
      );
  }

  backorderCartModal(cart: any = null, entries: any): void {
    this.add2CartBtn.classList.remove('rotating', 'disabled');
    this.showSpinnerPopover = false;
    this.cdr.detectChanges();

    this.modalService
      .openDialog(CUSTOM_LAUNCH_CALLER.BACKORDER_CART_MODAL, this.el, this.vcr, {
        backorderEntries: cart,
        formEntries: entries,
      })
      ?.pipe(take(1))
      .subscribe();

    const backOrderModal = this.modalService.getClosedModalData().subscribe((result) => {
      if (result !== undefined) {
        if (result && result !== 0 && result !== 'Cross click') {
          this.componentService.setMiniCartLoading(false);
          if (result !== 'hasError') {
            this.showGlobalMessage(
              'cart.addToCartSuccess',
              GlobalMessageType.MSG_TYPE_CONFIRMATION,
              entries.orderEntries[0].product.code
            );
          }
        }
        backOrderModal.unsubscribe();
      }
    });
  }

  backorderCartCheck(orderFormDetails: any) {
    this.add2CartBtn = document.querySelector(
      'popover-container button.add-to-cart-primary.sku-' + orderFormDetails.code
    );
    const checkAdd2CartBtn = this.add2CartBtn.classList.contains('sku-' + orderFormDetails.code);

    if (!this.showSpinnerPopover && checkAdd2CartBtn) {
      this.add2CartBtn.classList.add('rotating', 'disabled');
      this.showSpinnerPopover = true;
      if (this.isBackOrderPopupEnabled) {
        const quantity = this.explodedViewFormGroup.get(orderFormDetails.code).value;
        const body = {
          orderEntries: [
            {
              quantity,
              product: {
                code: orderFormDetails.code,
              },
            },
          ],
        };

        const entries = this.orderHistoryService.getInventory(body);

        entries.subscribe(
          (data) => {
            if (data.inventoryBackorderList.length > 0) {
              this.backorderCartModal(data, body);
            } else {
              this.addToCart(orderFormDetails);
            }
          },
          (error) => {
            this.add2CartBtn.classList.remove('rotating', 'disabled');
            this.showSpinnerPopover = false;
            this.showGlobalMessage('cart.genericError', GlobalMessageType.MSG_TYPE_ERROR);
          }
        );
      } else {
        this.addToCart(orderFormDetails);
      }
    }
  }
  checkStockLevel(stock: number, status: string): string {
    if (status === 'discontinued') {
      return this.isDiscontinuedEnabled ? 'discontinued' : 'outOfStock';
    }
    if (status === 'backorder' && stock <= 0) {
      return 'outOfStock';
    }
    return status === 'backorder' ? 'inStock' : status;
  }

  ngOnDestroy() {
    this.isDiscontinuedEnabledSubscription$?.unsubscribe();
    if (this.cartAddEntrySuccessSubscription$) {
      this.cartAddEntrySuccessSubscription$.unsubscribe();
    }
    if (this.backOrderModalSubscription) {
      this.backOrderModalSubscription.unsubscribe();
    }
    if (this.plpPopupModalEnabled) {
      this.plpPopupModalEnabled.unsubscribe();
    }
  }
}
